import { CsvHeader, CsvTemplateData } from '../constants/csv-importer';
import { CsvImportedDataRow, CsvImportedPost } from '../models/csv-importer';
import {
  GraphQLTypes,
  PostSource,
  ThirdPartyPost,
  ThirdPartyPostAttachmentType,
  ThirdPartyPostMedia,
  ThirdPartyPostMediaType
} from '../models/graphql';
import twttr from 'twitter-text';
import { getAllApolloCacheEntitiesOfType } from './apolloCacheHelpers';
import { getCache } from '../apollo/Cache';
import { addMonths, format, setMinutes } from 'date-fns';
import { DateTimeStringFormatWithoutSeconds } from '../constants/time-formats';

// Converts a CSV field to a double-quoted string to allow for .join() operation
export const csvSafeString = (field: string | number | null | undefined): string => {
  const safeField = field !== null && field !== undefined ? String(field) : '';
  const escapedField = safeField.replace(/"/g, '""');

  // Wrap the field in double quotes
  return `"${escapedField}"`;
};

// Note: Currently we only manage category, but we should consider managing other aspects
// of the data to ensure better coverage and reliability of the template provided.
export const generateCSVTemplate = () => {
  const categories = getAllApolloCacheEntitiesOfType(getCache(), GraphQLTypes.Category);
  const firstCategoryName = categories.find(c => !c.archived)?.name || 'General';

  const template = CsvTemplateData.map(row => {
    row[4] = row[4] === '[CATEOGRY_PLACEHOLDER]' ? firstCategoryName : row[4];

    return row;
  });

  const csvContent: string = [CsvHeader, ...template].map(row => row.map(csvSafeString).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blob);
  link.download = 'template.csv';

  link.click();
};

export const generateCSVTemplate2 = () => {
  const categories = getAllApolloCacheEntitiesOfType(getCache(), GraphQLTypes.Category);

  const template = categories.map((category, index) => {
    const date = setMinutes(addMonths(new Date(), 3 + Math.random() * 3), Math.floor(Math.random() * 12) * 5);

    const idx: number = index + 1;

    return [
      `Post text ${idx}`,
      `https://example.com/${idx}`,
      `https://example.com/image${idx}.jpg`,
      `${format(date, DateTimeStringFormatWithoutSeconds)}`,
      category.name,
      '',
      '',
      'First Comment'
    ];
  });

  const csvContent = [CsvHeader, ...template].map(e => e.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blob);
  link.download = 'template.csv';

  link.click();
};

export const imagesStringToStringArray = (images: string) => {
  return images.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(img => img.replace(/^"|"$/g, ''));
};

export const mapCsvImportedDataToPost = (csvImportedData: CsvImportedDataRow[]): CsvImportedPost[] => {
  return csvImportedData.map(post => ({
    text: post[0]?.trim() ?? '',
    link: post[1]?.trim() ?? '',
    images: post[2]?.trim() ?? '',
    scheduledTime: post[3]?.trim() ?? '',
    category: post[4]?.trim() ?? '',
    evergreen: post[5]?.trim() ?? '',
    title: post[6]?.trim() ?? '',
    firstComment: post[7]?.trim() ?? '' // Provide a default value in case it's missing
  }));
};

export const mapCsvImportedPostsToThirdPartyPost = (posts: CsvImportedPost[]): ThirdPartyPost[] => {
  const emptyPost: ThirdPartyPost = {
    __typename: 'ThirdPartyPost',
    source: PostSource.CSV_BULK,
    externalId: '',
    externalProfile: {
      __typename: 'ThirdPartyProfile',
      displayName: '',
      username: ''
    },
    platform: '',
    createdAt: new Date(),
    text: '',
    media: [],
    textMentions: [],
    attachmentTypes: [],
    postUrl: ''
  };

  return posts.map(post => {
    const { text, link, images } = post;

    const mappedImages: ThirdPartyPostMedia[] = imagesStringToStringArray(images).map(url => ({
      __typename: 'ThirdPartyPostMedia',
      type: ThirdPartyPostMediaType.PHOTO,
      url: url.trim(),
      thumbnailUrl: url.trim()
    }));

    const linkInTextArr = twttr.extractUrls(text);
    const linkInText = linkInTextArr.length ? linkInTextArr[0] : '';

    const linkUrl = link.trim() || linkInText;

    return {
      ...emptyPost,
      text,
      linkUrl,
      attachmentTypes: mappedImages.length ? [ThirdPartyPostAttachmentType.PHOTO] : [],
      media: mappedImages
    };
  });
};
